import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';

import {
  AdminSearchUsersForRecoveryQuery,
  AdminSearchUsersForRecoveryQueryVariables,
  AdminSendSignInLinkToUserMutation,
  AdminSendSignInLinkToUserMutationVariables,
  AdminUserSummaryFieldsFragment,
} from '../../../../__gqltypes__';
import LinkEmailCredentialModal from '../popups/LinkEmailCredentialModal';
import { ADMIN_SEND_SIGN_IN_LINK, SEARCH_USERS_FOR_RECOVERY } from '../recovery/graphql';

export default function AdminAccountRecovery(): JSX.Element {
  const [query, setQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUserSummaryFieldsFragment | null>(null);
  const [sendLinkTarget, setSendLinkTarget] = useState<AdminUserSummaryFieldsFragment | null>(null);
  const [sendLinkResult, setSendLinkResult] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);

  const [runSearch, { data, loading, error, called }] = useLazyQuery<
    AdminSearchUsersForRecoveryQuery,
    AdminSearchUsersForRecoveryQueryVariables
  >(SEARCH_USERS_FOR_RECOVERY, { fetchPolicy: 'network-only' });

  const [sendSignInLink, { loading: sendingLink }] = useMutation<
    AdminSendSignInLinkToUserMutation,
    AdminSendSignInLinkToUserMutationVariables
  >(ADMIN_SEND_SIGN_IN_LINK);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    runSearch({ variables: { input: { query: trimmed } } });
  };

  const handleModalSuccess = () => {
    if (query.trim()) {
      runSearch({ variables: { input: { query: query.trim() } } });
    }
  };

  const handleSendLinkConfirm = async () => {
    if (!sendLinkTarget) return;
    try {
      const { data: result } = await sendSignInLink({
        variables: { input: { userId: sendLinkTarget.userId } },
      });
      if (result?.adminSendSignInLinkToUser.success) {
        setSendLinkResult({
          variant: 'success',
          text: result.adminSendSignInLinkToUser.message ?? 'Sign-in link sent.',
        });
      } else {
        setSendLinkResult({
          variant: 'danger',
          text: result?.adminSendSignInLinkToUser.message ?? 'Unknown error.',
        });
      }
    } catch (err) {
      setSendLinkResult({ variant: 'danger', text: (err as Error).message });
    }
  };

  const users = data?.adminSearchUsersForRecovery.users ?? [];

  return (
    <div>
      <Card className="mb-4">
        <Card.Header>Account Recovery</Card.Header>
        <Card.Body>
          <Alert variant="info">
            Help users who can&apos;t log in. <strong>Send Link</strong> emails a magic sign-in link.{' '}
            <strong>Recover</strong> adds a new email to a locked-out account.
          </Alert>

          <Form onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label>Search</Form.Label>
              <div className="d-flex">
                <Form.Control
                  type="text"
                  placeholder="userId, display name, or email"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  disabled={loading}
                />
                <Button type="submit" variant="primary" className="ml-2" disabled={loading || !query.trim()}>
                  Search
                </Button>
              </div>
            </Form.Group>
          </Form>

          {loading && <Spinner animation="border" className="m-2" />}

          {error && (
            <Alert variant="danger" className="mt-3">
              {error.message}
            </Alert>
          )}

          {!loading && called && !error && (
            <>
              {users.length === 0 ? (
                <Alert variant="secondary" className="mt-3">
                  No users found.
                </Alert>
              ) : (
                <Table responsive className="mt-3">
                  <thead>
                    <tr>
                      <th>Display name</th>
                      <th>Email</th>
                      <th>Firebase email</th>
                      <th>Providers</th>
                      <th>Role</th>
                      <th>User ID</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.userId}>
                        <td>{u.displayName}</td>
                        <td>{u.email ?? <em className="text-muted">(none)</em>}</td>
                        <td>{u.firebaseEmail ?? <em className="text-muted">(none)</em>}</td>
                        <td>
                          {u.providers.length > 0 ? (
                            u.providers.map((p) => p.type).join(', ')
                          ) : (
                            <em className="text-muted">(none)</em>
                          )}
                        </td>
                        <td>{u.role}</td>
                        <td>
                          <code>{u.userId}</code>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="info"
                            className="mr-2"
                            disabled={!u.email}
                            title={u.email ? 'Send sign-in link' : 'No email on account'}
                            onClick={() => {
                              setSendLinkResult(null);
                              setSendLinkTarget(u);
                            }}
                          >
                            Send Link
                          </Button>
                          <Button size="sm" variant="warning" onClick={() => setSelectedUser(u)}>
                            Recover
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {selectedUser && (
        <Modal show={!!selectedUser} onHide={() => setSelectedUser(null)} size="lg">
          <LinkEmailCredentialModal
            user={selectedUser}
            onHide={() => setSelectedUser(null)}
            onSuccess={handleModalSuccess}
          />
        </Modal>
      )}

      {sendLinkTarget && (
        <Modal show={!!sendLinkTarget} onHide={() => sendingLink || setSendLinkTarget(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Send sign-in link</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Send sign-in link to <strong>{sendLinkTarget.email}</strong>?
            </p>
            <p className="text-muted small mb-0">
              {sendLinkTarget.displayName} · <code>{sendLinkTarget.userId}</code>
            </p>
            {sendLinkResult && (
              <Alert variant={sendLinkResult.variant} className="mt-3 mb-0">
                {sendLinkResult.text}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            {sendLinkResult ? (
              <Button variant="primary" onClick={() => setSendLinkTarget(null)}>
                OK
              </Button>
            ) : (
              <Button variant="info" onClick={handleSendLinkConfirm} disabled={sendingLink}>
                {sendingLink ? 'Sending...' : 'Send link'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
